import { Box } from '@mui/material';
import { useIntl } from 'react-intl';

import {
  ApiWorkflowDialog,
  type ApiWorkflowDialogMessages,
  TextField,
} from '@verantos-dev/shared-components';

import { useRenameAnalysisDialog } from '../hooks/useRenameAnalysisDialog';

const NAME_MAX_CHARACTERS = 100;

interface RenameAnalysisDialogProps {
  projectId: string;
  analysisProfileId: string;
  currentName: string;
  onClose: () => void;
}

/**
 * Inline rename dialog opened from an analysis row on the artifacts page. Uses the
 * `analysisList.renameModal.*` message ids — registered app-wide via
 * `analysisMessageKeysEn` — so the copy is owned by feature-analysis without this
 * lib importing it.
 */
export const RenameAnalysisDialog = ({
  projectId,
  analysisProfileId,
  currentName,
  onClose,
}: RenameAnalysisDialogProps) => {
  const { formatMessage } = useIntl();
  const { name, onChangeName, onSubmit, closeDialog, isPending, submittingError, isValidToSubmit } =
    useRenameAnalysisDialog({ projectId, analysisProfileId, currentName, onClose });

  const dialogMessages: ApiWorkflowDialogMessages = {
    entryCancelButtonText: formatMessage({ id: 'analysisList.renameModal.cancelButton' }),
    entrySaveButtonText: formatMessage({ id: 'analysisList.renameModal.renameButton' }),
    submittingCancelButtonText: formatMessage({ id: 'analysisList.renameModal.cancelButton' }),
    submittingSaveButtonText: formatMessage({ id: 'analysisList.renameModal.submitting' }),
    successCloseButtonText: formatMessage({ id: 'analysisList.renameModal.closeButton' }),
    submittingErrorTitle: formatMessage({ id: 'analysisList.renameModal.submittingError' }),
    successTitle: formatMessage({ id: 'analysisList.renameModal.success.title' }),
    successText: formatMessage({ id: 'analysisList.renameModal.success.text' }, { name }),
  };

  return (
    <ApiWorkflowDialog
      id="renameAnalysisDialog"
      title={formatMessage({ id: 'analysisList.renameModal.title' })}
      dialogMessages={dialogMessages}
      onClose={closeDialog}
      onSubmit={onSubmit}
      isValidForSubmit={isValidToSubmit}
      isSubmitting={isPending}
      submittingError={submittingError}
      hideDisplayButtonsOnSuccess
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
        <TextField
          id="rename-analysis-name-input"
          displayLabel={formatMessage({ id: 'analysisList.renameModal.name.label' })}
          value={name}
          placeholder={formatMessage({ id: 'analysisList.renameModal.name.placeholder' })}
          onChange={onChangeName}
          maxCharacters={NAME_MAX_CHARACTERS}
          maxCharacterErrorMsg={formatMessage({
            id: 'analysisList.renameModal.maxCharacterErrorMsg',
          })}
          displayNumCharacter
          width="auto"
          beepMaxCharacters
        />
      </Box>
    </ApiWorkflowDialog>
  );
};

export default RenameAnalysisDialog;
